import { useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import {
  useGetSectionsQuery,
  useCreateSectionMutation,
  useUpdateSectionMutation,
  useGetFloorsQuery,
} from "../../store/api";

const Sections = () => {
  const [selectedFloorId, setSelectedFloorId] = useState("");
  const { data: floors } = useGetFloorsQuery();
  const {
    data: sections,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetSectionsQuery(selectedFloorId, {
    skip: !selectedFloorId,
  });
  const [createSection, createState] = useCreateSectionMutation();
  const [updateSection, updateState] = useUpdateSectionMutation();

  const [form, setForm] = useState({
    floorId: "",
    name: "",
    sort: 0,
    isActive: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [localError, setLocalError] = useState("");
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  const floorsList = Array.isArray(floors)
    ? floors.filter((f) => f.isActive !== false)
    : [];
  const sectionsList = Array.isArray(sections) ? sections : [];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");
    try {
      const payload = {
        ...form,
        sort: Number(form.sort),
      };
      if (editingId) {
        await updateSection({
          sectionId: editingId,
          payload,
        }).unwrap();
      } else {
        await createSection(payload).unwrap();
      }

      if (editingId) {
        setTimeout(() => {
          updateState.reset();
          createState.reset();
          setEditingId(null);
          setForm({ floorId: "", name: "", sort: 0, isActive: true });
          setShowForm(false);
        }, 1000);
      } else {
        setForm({ floorId: "", name: "", sort: 0, isActive: true });
        setShowForm(false);
        createState.reset();
        updateState.reset();
      }
      refetch();
    } catch (err) {
      // error handled by UI
    }
  };

  const handleEdit = (section) => {
    createState.reset();
    updateState.reset();
    setLocalError("");
    setEditingId(section.sectionId || section.id);
    setForm({
      floorId: section.floorId || "",
      name: section.name || "",
      sort: section.sort ?? 0,
      isActive: section.isActive !== undefined ? section.isActive : true,
    });
    setShowForm(true);
  };

  const handleToggleActive = async (section) => {
    setLocalError("");
    try {
      await updateSection({
        sectionId: section.sectionId || section.id,
        payload: {
          floorId: section.floorId || "",
          name: section.name || "",
          sort: section.sort ?? 0,
          isActive: !section.isActive,
        },
      }).unwrap();
      refetch();
    } catch (err) {
      setLocalError(
        err?.data?.detail ||
          err?.data?.message ||
          "Failed to update section status.",
      );
    }
  };

  const handleCancel = () => {
    createState.reset();
    updateState.reset();
    setLocalError("");
    setEditingId(null);
    setForm({ floorId: "", name: "", sort: 0, isActive: true });
    setShowForm(false);
  };

  const columns = [
    { key: "name", label: "Section Name" },
    { key: "floor", label: "Floor" },
    { key: "sort", label: "Sort Order" },
    { key: "status", label: "Status" },
    { key: "actions", label: "Actions" },
  ];

  const rows = sectionsList.map((section) => {
    const id = section.sectionId || section.id;
    const floor = floorsList.find(
      (f) => (f.floorId || f.id) === section.floorId,
    );
    return {
      id,
      name: section.name,
      floor: floor?.name || "—",
      sort: section.sort ?? 0,
      status: (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {section.isActive ? "Active" : "Inactive"}
        </span>
      ),
      actions: (
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="p-1 text-[#4f46e5] hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded transition"
            onClick={() => handleEdit(section)}
            title="Edit"
            aria-label="Edit section"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <input
            type="checkbox"
            checked={Boolean(section.isActive)}
            onChange={() => handleToggleActive(section)}
            disabled={updateState.isLoading}
            className="h-4 w-4 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800"
            aria-label={
              section.isActive
                ? `Mark ${section.name} inactive`
                : `Mark ${section.name} active`
            }
          />
        </div>
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sections"
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => {
                createState.reset();
                updateState.reset();
                setLocalError("");
                setEditingId(null);
                setForm({ floorId: "", name: "", sort: 0, isActive: true });
                setShowForm(true);
              }}
            >
              Add Section
            </Button>
          )
        }
      />

      {!showForm && (
        <Card className="p-0">
          {/* Filter Section */}
          <div className="border-b border-slate-100 dark:border-slate-700 px-6 py-4">
            <Select
              label="Select Floor"
              value={selectedFloorId}
              onChange={(e) => setSelectedFloorId(e.target.value)}
            >
              <option value="">Select a floor</option>
              {floorsList.map((floor) => (
                <option
                  key={floor.floorId || floor.id}
                  value={floor.floorId || floor.id}
                >
                  {floor.name}
                </option>
              ))}
            </Select>
          </div>

          {/* Table Section */}
          {selectedFloorId ? (
            <div className="px-6 py-4">
              {localError && <Alert tone="error">{localError}</Alert>}
              {isLoading && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Loading sections...
                </p>
              )}
              {isError && (
                <Alert tone="error">
                  {error?.data?.detail || "Failed to load sections."}
                </Alert>
              )}
              {!isLoading && !isError && (
                <Table
                  columns={columns}
                  rows={rows}
                  emptyText="No sections yet."
                />
              )}
            </div>
          ) : (
            <div className="px-6 py-4">
              <Alert tone="info">Please select a floor to view sections.</Alert>
            </div>
          )}
        </Card>
      )}

      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {editingId ? "Edit Section" : "Add Section"}
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <Select
              label="Floor"
              name="floorId"
              value={form.floorId}
              onChange={handleChange}
              required
            >
              <option value="">Select a floor</option>
              {floorsList.map((floor) => (
                <option
                  key={floor.floorId || floor.id}
                  value={floor.floorId || floor.id}
                >
                  {floor.name}
                </option>
              ))}
            </Select>

            <div className="grid gap-4 md:grid-cols-3">
              <Input
                label="Section Name"
                name="name"
                placeholder="Main Dining"
                value={form.name}
                onChange={handleChange}
                required
              />
              <Input
                label="Sort Order"
                name="sort"
                type="number"
                placeholder="0"
                value={form.sort}
                onChange={handleChange}
                helper="Lower numbers appear first"
              />
              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={form.isActive}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-[#4f46e5] focus:ring-[#4f46e5] dark:bg-slate-700"
                />
                Active
              </label>
            </div>

            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Section {editingId ? "updated" : "created"} successfully.
              </Alert>
            )}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {createState.error?.data?.detail ||
                  updateState.error?.data?.detail ||
                  "Failed to save section."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Section
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
        title="Error"
        confirmText="OK"
        onConfirm={() => setErrorModal({ isOpen: false, message: "" })}
      >
        <p>{errorModal.message}</p>
      </Modal>
    </div>
  );
};

export default Sections;
